import { createElement } from 'react';
import Box from '@mui/material/Box';
import EmptyTargetFieldsImage from '../../images/empty-target-fields';
import type { SettingsLayout } from './types';
import type {
  FastFillTargetFieldsEnvironment,
  FastFillTargetSelection,
} from './fast-fill-settings-adapter';

const legacySelectionPrefixFor = (
  stableTargetFieldKeys: string[],
  legacyTargetDataNames: string[],
): string => {
  let prefix = '__fastfill_legacy_ai_prompt__:';
  let collides = true;

  while (collides) {
    collides = false;
    for (const dataName of legacyTargetDataNames) {
      if (stableTargetFieldKeys.includes(`${prefix}${dataName}`)) {
        prefix += '_';
        collides = true;
        break;
      }
    }
  }

  return prefix;
};

const fastFillTargetFieldsLayout: SettingsLayout<
FastFillTargetSelection,
FastFillTargetFieldsEnvironment
> = ({ draft, environment }) => {
  const stableTargetFieldKeys = [
    ...draft.selectedTargetFieldKeys,
    ...environment.eligibleTargetFields.map(({ key }) => key),
    ...environment.inactiveSelectedTargetFields.map(({ key }) => key),
  ];
  const inactiveSelectedTargetFields = environment.inactiveSelectedTargetFields.filter(
    ({ key }) => draft.selectedTargetFieldKeys.includes(key),
  );
  const selectedLegacyTargetDataNames = draft.selectedLegacyTargetDataNames ?? [];
  const selectedTargetFieldKeySet = new Set(draft.selectedTargetFieldKeys);
  const selectedLegacyTargetDataNameSet = new Set(selectedLegacyTargetDataNames);
  const legacySelectionPrefix = legacySelectionPrefixFor(
    stableTargetFieldKeys,
    selectedLegacyTargetDataNames,
  );
  const legacyPromptOrder = draft.legacyPromptOrder ?? [];
  const orderedSelectedValues = legacyPromptOrder.flatMap((target) => {
    if (target.kind === 'field') {
      return selectedTargetFieldKeySet.has(target.key) ? [target.key] : [];
    }

    return selectedLegacyTargetDataNameSet.has(target.dataName)
      ? [`${legacySelectionPrefix}${target.dataName}`]
      : [];
  });
  const orderedFieldKeys = new Set(legacyPromptOrder.flatMap((target) => (
    target.kind === 'field' && selectedTargetFieldKeySet.has(target.key) ? [target.key] : []
  )));
  const orderedLegacyNames = new Set(legacyPromptOrder.flatMap((target) => (
    target.kind === 'unresolved' && selectedLegacyTargetDataNameSet.has(target.dataName)
      ? [target.dataName]
      : []
  )));

  return [{
    kind: 'selectable-list',
    id: 'fast-fill-target-fields',
    items: [
      ...environment.eligibleTargetFields,
      ...inactiveSelectedTargetFields,
    ].map((field) => ({
      value: field.key,
      primaryText: field.label,
      secondaryText: field.dataName,
      ...(field.description ? { description: field.description } : {}),
      trailingText: field.displayType,
    })),
    selectedValues: [
      ...orderedSelectedValues,
      ...draft.selectedTargetFieldKeys.filter((key) => !orderedFieldKeys.has(key)),
      ...selectedLegacyTargetDataNames
        .filter((dataName) => !orderedLegacyNames.has(dataName))
        .map((dataName) => `${legacySelectionPrefix}${dataName}`),
    ],
    searchPlaceholder: 'Search',
    maxHeight: 60,
    emptyState: {
      title: 'No available target fields',
      description: "Photo FastFill can only target Text, Numeric, Yes/No, Date, Time, Choice, Classification, Address, and Status fields. This form doesn't have any.",
      illustration: createElement(
        Box,
        {
          'data-testid': 'fast-fill-target-fields-empty-state-illustration',
          sx: {
            '& svg': {
              display: 'block',
              height: (theme) => theme.spacing(22.5),
              width: (theme) => theme.spacing(33),
            },
          },
        },
        createElement(EmptyTargetFieldsImage),
      ),
    },
    warnings: [
      ...inactiveSelectedTargetFields.map(
        ({ dataName }) => `${dataName} is inactive. Clear it to remove it.`,
      ),
      ...environment.unresolvedTargetFieldKeys.map(
        (key) => `${key} is no longer available.`,
      ),
      ...(environment.unresolvedLegacyTargetDataNames ?? []).map(
        (dataName) => `${dataName} is no longer available.`,
      ),
    ],
    onChange: (selectedValues) => ({
      ...draft,
      selectedTargetFieldKeys: selectedValues.filter(
        (value) => !value.startsWith(legacySelectionPrefix),
      ),
      selectedLegacyTargetDataNames: selectedValues
        .filter((value) => value.startsWith(legacySelectionPrefix))
        .map((value) => value.slice(legacySelectionPrefix.length)),
      legacyPromptOrder: selectedValues.map((value) => (
        value.startsWith(legacySelectionPrefix)
          ? {
            kind: 'unresolved' as const,
            dataName: value.slice(legacySelectionPrefix.length),
          }
          : { kind: 'field' as const, key: value }
      )),
    }),
  }];
};

export type { FastFillTargetSelection } from './fast-fill-settings-adapter';

export default fastFillTargetFieldsLayout;
